// `slim-header-dropdown/tooltip/slim.header.dropdown.tooltip.tpl.html` — the panel the slim
// alert row opens: "Add search" plus the other alerts you can switch to.
//
// ⚠️ UPSTREAM HAS TWO MUTUALLY EXCLUSIVE FORMS OF THIS PANEL AND BOTH ARE HERE. The template
// branches on `automatedHuntersEnabled && extensionProduct === Candidates`:
//
//   - WITHOUT automation (or on the contacts product): "Add search" and a flat list of the
//     other alerts.
//   - WITH automation, on candidates: a search input over TWO collapsible groups — "Job
//     searches", fed by `extension-job/search`, and "My searches", the alerts that are not
//     bound to a job.
//
// The flag has no default on purpose. It selects between two entirely different panels, and a
// caller that guesses gets a confidently-rendered wrong one; `automatedHuntersEnabled` comes
// from `window.careero.featureFlags[AutomatedHunters]` (component:128), which is tenant
// configuration and not something this component can infer.
//
// The three filter expressions the automated form needs are in `dropdownGroups.ts` with their
// tests — two of them appear twice upstream, once in a list and once in the count beside its
// header, and the two have to agree.
import type { ReactElement } from 'react'
import { useState } from 'react'
import { ExtensionAlertTab, AlertTabState, type AlertTabData } from './ExtensionAlertTab.tsx'
import type { SlimHeaderAlert } from './slimHeaderControls.ts'
import {
  DropdownGroup,
  groupStateForSearch,
  INITIAL_GROUP_STATE,
  jobRows,
  mySearches,
  type GroupState,
} from './dropdownGroups.ts'

export interface SlimHeaderDropdownPanelProps {
  /** Every alert for this user and host, in the order the API returned them. */
  alerts: readonly SlimHeaderAlert[]
  /** `$stateParams.alertId` — this one is omitted from the list, being already open. */
  currentAlertId?: number
  /** Per-alert counts, keyed by alert id. */
  tabData?: Readonly<Record<number, AlertTabData>>
  /** `automatedHuntersEnabled && product === Candidates`. No default — see the note above. */
  automatedHunters: boolean
  /**
   * `shortlistHunterAutomationService.isEnabled` —
   * `featureFlags[HUNTER_AUTOMATION] && careero.hunterAutomationEnabled`.
   *
   * ⚠️ A DIFFERENT FLAG FROM `automatedHunters`, AND CONFLATING THEM HID THE BADGE ON
   * CONTACTS. `automated-hunters-enabled` is `AUTOMATED_HUNTERS && product === Candidates` and
   * decides whether this panel has a JOB-SEARCH GROUP at all; the dot beside each search name
   * is `extension-alert-tab.tpl.html:9`, which asks the automation SERVICE, and the service
   * knows nothing about products. So the contacts panel correctly has no job group and
   * correctly DOES have the dot — measured against build 354, whose contacts row renders
   * `hunter-automation-mode mode-disabled`.
   */
  automationEnabled?: boolean
  entityPlural?: string
  onAddSearch?: () => void
  onSelectAlert?: (alert: SlimHeaderAlert) => void
  /** `extensionJobsSearchResponse` — the automated form's Jobs group. */
  jobs?: { results: readonly PanelJob[]; totalResultCount: number }
  /** The alert currently open is bound to this job, which the Jobs group then omits. */
  currentExtensionJobId?: number | null
  /** `ctrl.searching` — the magnifier becomes a spinner while the jobs call is in flight. */
  searching?: boolean
  onSearch?: (query: string) => void
  onSelectJob?: (job: PanelJob) => void
}

/** `IExtensionJobDTO`, narrowed to the fields the job rows render. */
export interface PanelJob {
  id: number
  title: string
  externalJobId: string
}

export function SlimHeaderDropdownPanel({
  alerts,
  currentAlertId,
  tabData,
  automatedHunters,
  automationEnabled = false,
  entityPlural = 'candidates',
  onAddSearch,
  onSelectAlert,
  jobs,
  currentExtensionJobId,
  searching = false,
  onSearch,
  onSelectJob,
}: SlimHeaderDropdownPanelProps): ReactElement {
  const [searchInput, setSearchInput] = useState('')
  const [groups, setGroups] = useState<GroupState>(INITIAL_GROUP_STATE)

  const applySearch = (value: string): void => {
    setSearchInput(value)
    // Upstream sets the folds from the input on EVERY change, so a manual fold is discarded
    // the next time you type — see `groupStateForSearch`.
    setGroups(groupStateForSearch(value))
    onSearch?.(value)
  }

  const toggleGroup = (group: DropdownGroup): void => {
    setGroups((previous) => ({ ...previous, [group]: !previous[group] }))
  }
  // `ng-if="… extensionAlertsDisplay.length > 1"` on BOTH the separator and the list: with one
  // alert there is nothing to switch to, so the panel is the "Add search" row alone.
  const others = alerts.filter((alert) => alert.id !== currentAlertId)
  const hasOthers = alerts.length > 1

  return (
    <div className="slim-header-dropdown-container nicer-scroller four-px-scroll">
      <div className="slim-header-dropdown-more-options">
        <span
          className="slim-header-dropdown-etc slim-header-dropdown-add-hunter-header"
          role="button"
          aria-label="Add search"
          tabIndex={0}
          onClick={onAddSearch}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            onAddSearch?.()
          }}
        >
          {/* The `&nbsp;&nbsp;` is upstream's spacing and it renders — see the note in
              ShortlistHeader about JSX and whitespace. */}
          <i className="fas fa-plus" />
          {'   Add search'}
        </span>
        {hasOthers ? <span className="slim-header-dropdown-more-options-sep" /> : null}
      </div>

      {automatedHunters ? (
        <div className="slim-header-dropdown-alerts">
          <div className="search-wrapper">
            <i
              className={`wrapper-icon search-icon fa ${searching ? 'fa-spinner fa-spin' : 'fa-search'}`}
              aria-hidden="true"
            />
            {/* ⚠️ `ng-empty` IS A STYLE HOOK HERE, NOT FRAMEWORK BOOKKEEPING, and this port
                was not supplying it. `ngModel` adds the class whenever the bound value is
                empty, and `_slim-header-dropdown-tooltip.scss:134` answers with
                `padding-right: 0` — which is the room the placeholder needs, because the base
                rule is `padding: 0 36px` on a 179px border-box field and the 36px on the RIGHT
                is reserved for the clear × that only exists while there IS a query. Without
                the class the field keeps both paddings forever and "Job ID, Title, etc..." is
                cut off. Measured beside build 354: prod computes `0px 0px 0px 36px` on an
                empty field and this computed `0px 36px`.

                `ng-not-empty` is its pair — nothing in the ported styles selects it, but
                `ngModel` sets exactly one of the two and a half-transcription is how the next
                rule that does select it gets missed. Same contract as `uibDropdown`'s `open`. */}
            <input
              type="search"
              id="slim-header-dropdown-search-input"
              className={`search-input ${searchInput.length === 0 ? 'ng-empty' : 'ng-not-empty'}`}
              placeholder="Job ID, Title, etc..."
              value={searchInput}
              onChange={(event) => applySearch(event.target.value)}
              // `ng-click="onSearchInputClick($event)"` stops the click closing the panel it
              // sits inside; the same is needed here for the same reason.
              onClick={(event) => event.stopPropagation()}
            />
            {searchInput.length > 0 ? (
              <i
                className="wrapper-icon clear-icon fa fa-close"
                role="button"
                aria-label="Clear search"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation()
                  applySearch('')
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  applySearch('')
                }}
              />
            ) : null}
          </div>

          <div className="group-wrapper">
            <div
              className="group-header"
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation()
                toggleGroup(DropdownGroup.Jobs)
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                toggleGroup(DropdownGroup.Jobs)
              }}
            >
              <i className={`group-fold-icon fa ${groups[DropdownGroup.Jobs] ? 'fa-caret-down' : 'fa-caret-right'}`} />
              <span className="group-title">Job searches</span>
              <span className="group-count">
                {/* A FILTERED numerator over an UNFILTERED denominator — the rows exclude the
                    job the current alert is bound to, the total does not. Deriving one from
                    the other would quietly change the number production shows. */}
                <span>
                  ({jobRows(jobs?.results ?? [], currentExtensionJobId).length}/{jobs?.totalResultCount ?? 0})
                </span>
                <i
                  className="cursor-pointer info-btn fa fa-info-circle"
                  title="Matching automated jobs / Total matching automated jobs"
                />
              </span>
            </div>
            {groups[DropdownGroup.Jobs] ? (
              <div className="group-content">
                {jobRows(jobs?.results ?? [], currentExtensionJobId).map((job) => (
                  <div
                    key={job.id}
                    className="shortlist-tab"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectJob?.(job)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onSelectJob?.(job)
                    }}
                  >
                    {/* A job row is NOT an `extension-alert-tab`: upstream writes this markup
                        inline, with the alert-tab classes but a different inner shape — title
                        over the ATS id behind a briefcase icon, and no counts. The commented-out
                        `<extension-alert-tab>` above it upstream is a road not taken. */}
                    <div className="extension-alert-tab-container extension-alert-job-tab-container">
                      <div className="extension-alert-tab-content extension-alert-tab-name">
                        <span className="text-container" title={job.title}>
                          {job.title}
                        </span>
                      </div>
                      <div className="extension-alert-tab-content extension-alert-tab-content-secondary extension-alert-tab-employer-name">
                        <i className="extension-alert-tab-icon fas fa-briefcase" />
                        <span>{job.externalJobId}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* ⚠️ THE TOGGLE IS ON THE WRAPPER HERE AND ON THE HEADER ABOVE, which looks like a
              transcription slip upstream and is not one to copy over: `slim.header.dropdown.
              tooltip.tpl.html:45` puts `ng-click` on the Jobs group's `.group-header` and :93
              puts it on the Alerts group's `.group-wrapper`. So in production the whole "My
              searches" block folds when you click its empty space, and the "Job searches"
              block only folds from its title row. Ported as found. */}
          <div
            className="group-wrapper"
            onClick={(event) => {
              event.stopPropagation()
              toggleGroup(DropdownGroup.Alerts)
            }}
          >
            <div
              className="group-header"
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                toggleGroup(DropdownGroup.Alerts)
              }}
            >
              <i className={`group-fold-icon fa ${groups[DropdownGroup.Alerts] ? 'fa-caret-down' : 'fa-caret-right'}`} />
              <span className="group-title">My searches</span>
              <span className="group-count">
                <span>({mySearches(alerts, searchInput, currentAlertId).length})</span>
                <i className="cursor-pointer info-btn fa fa-info-circle" title="Manually created searches" />
              </span>
            </div>
            {groups[DropdownGroup.Alerts] ? (
              <div className="group-content">
                {mySearches(alerts, searchInput, currentAlertId).map((alert) => (
                  <div
                    key={alert.id}
                    className="shortlist-tab"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectAlert?.(alert)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onSelectAlert?.(alert)
                    }}
                  >
                    <ExtensionAlertTab
                      alert={alert}
                      state={AlertTabState.View}
                      currentAlertId={currentAlertId}
                      tabData={tabData?.[alert.id]}
                      // ⚠️ `automationEnabled` GATES THE DOT ENTIRELY, and the panel had the
                      // flag and did not pass it. Upstream's rows ARE `<extension-alert-tab>`,
                      // so every search in the list carries the same automation marker the
                      // open tab does — the small ● after the name. Without it a recruiter
                      // cannot tell which of their searches is automated from the list they
                      // switch between them in.
                      automationEnabled={automationEnabled}
                      entityPlural={entityPlural}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : hasOthers ? (
        <div className="slim-header-dropdown-alerts">
          {others.map((alert) => (
            <div
              key={alert.id}
              className="shortlist-tab"
              role="button"
              tabIndex={0}
              onClick={() => onSelectAlert?.(alert)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onSelectAlert?.(alert)
              }}
            >
              <ExtensionAlertTab
                alert={alert}
                state={AlertTabState.View}
                currentAlertId={currentAlertId}
                tabData={tabData?.[alert.id]}
                automationEnabled={automationEnabled}
                entityPlural={entityPlural}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
